import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bool, func, string } from 'prop-types';
import { partition } from 'lodash-es';

import { withLang } from '../../../i18n';
import { surveyFieldIds } from '../../../constants';
import { notificationType } from '../../../constants/types';
import useSurveyParticipantsInput from '../useSurveyParticipantsInput';
import {
  addParticipantsByEmailAction
} from '../../../actions';
import IsRequiredField from '../../IsRequiredField';
import ParticipantList from '../ParticipantList';
import Notification from '../../Notification';
import styles from '../surveyParticipantsEditor.module.css';

const TeacherParticipantsEditor = ({
  translate: t, showAsRequired, showProblems, isInvalid, surveyType
}) => {
  const dispatch = useDispatch();
  const participants = useSelector((state) => state.survey.surveyOptions.participants);

  const {
    inputValue, inputRows, handleChange, resetInput, splitInputValue
  } = useSurveyParticipantsInput();
  const [duplicateSubmittedEmails, setDuplicateSubmittedEmails] = useState([]);

  const onParticipantsSubmit = async (emails) => {
    const checkIsAlreadyAddedEmail = (email) => participants.map((p) => p.email).includes(email);
    const [alreadyAddedEmails, newEmails] = partition(emails, checkIsAlreadyAddedEmail);

    if (newEmails.length) {
      dispatch(addParticipantsByEmailAction(newEmails));
    }
    setDuplicateSubmittedEmails(alreadyAddedEmails);
  };

  const processParticipants = async (event) => {
    event.preventDefault();
    const emails = splitInputValue();
    await onParticipantsSubmit(emails);
    resetInput();
  };

  const renderMessages = () => {
    const addedCount = participants.filter((p) => !p.error && p.new).length;
    const alreadyAddedCount = duplicateSubmittedEmails.length;
    const notFoundCount = participants.filter((p) => p.error).length;

    const getNotificationWithAmount = (amount, pluralTextKey, textKey, type) => {
      if (!amount) return null;
      return <Notification type={type} text={`${amount} ${t(`surveyParticipantsEditor.${amount > 1 ? pluralTextKey : textKey}`)}`} />;
    };

    return (
      <div>
        {getNotificationWithAmount(addedCount, 'teacherParticipantsEditor.participantsAdded', 'teacherParticipantsEditor.participantAdded', notificationType.OK)}
        {getNotificationWithAmount(alreadyAddedCount, 'participantsAlreadyAdded', 'participantAlreadyAdded', notificationType.OK)}
        {getNotificationWithAmount(notFoundCount, 'teacherParticipantsEditor.participantsRejected', 'teacherParticipantsEditor.participantRejected', notificationType.ERROR)}
      </div>
    );
  };

  const editorContainerClass = showProblems && isInvalid ? `${styles.formItem} formError` : styles.formItem;
  const totalParticipantCount = participants.length;

  const noBottomMargin = { marginBottom: 0 };
  return (
    <div>
      <div className={editorContainerClass}>
        <label htmlFor="teacherParticipantsInput">
          {t('surveyParticipantsEditor.teacherParticipantsEditor.searchLabel')}
          {showAsRequired && <IsRequiredField />}
        </label>
        <textarea
          id={surveyFieldIds.PARTICIPANTS_INPUT}
          placeholder={t('surveyParticipantsEditor.teacherParticipantsEditor.placeholder')}
          value={inputValue}
          onChange={handleChange}
          rows={inputRows}
          data-cy="teacher-participants-text-area"
        />
        <div className={styles.addButtonContainerDescription}>
          <p className={styles.addParticipantsDescription}>{t('surveyParticipantsEditor.teacherParticipantsEditor.description')}</p>
          <button
            type="button"
            id="addParticipantsButton"
            className={`button--action ${styles.addButton}`}
            onClick={processParticipants}
            data-cy="add-participants-button"
          >
            {t('common.add')}
          </button>
        </div>
        <div className={styles.description}>
          {renderMessages()}
        </div>
      </div>
      <ParticipantList
        count={totalParticipantCount}
        surveyType={surveyType}
        participants={participants}
      />
      <p style={noBottomMargin}>{t('surveyParticipantsEditor.totalParticipants', { totalParticipantCount })}</p>
    </div>
  );
};

TeacherParticipantsEditor.propTypes = {
  showAsRequired: bool.isRequired,
  surveyType: string.isRequired,
  showProblems: bool,
  isInvalid: bool,
  translate: func.isRequired
};

TeacherParticipantsEditor.defaultProps = {
  showProblems: false,
  isInvalid: false
};

export default withLang(TeacherParticipantsEditor);
